/* eslint-disable @typescript-eslint/no-explicit-any */
declare let google: any
declare let window: any
import { useRef, useCallback, useEffect, useState } from 'react'
import { useScript } from 'usehooks-ts'
import type { Status } from 'usehooks-ts'
import log from 'loglevel'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { useThrobberContext } from 'src/Throbber/ThrobberContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useGoogleDriveContext } from '../../context/GoogleDriveContext'
import {
  PickerConfiguration,
  PickerResponse,
  GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
  GOOGLE_API_SRC,
  GOOGLE_GSI_CLIENT_SRC,
} from './GoogleDriveWizard.constants'
import {
  useGoogleUserToken,
  GoogleUserTokenError,
  GOOGLE_USER_CANCELLED_CODES,
} from './GoogleDriveUserToken.hooks'
import {
  useCheckReceptorFileAccess,
  splitByReceptorAccess,
  buildDeniedAccessMessage,
} from './GoogleDriveAccess.hooks'
import { useGoogleDrivePickerConfig } from './GoogleDrivePickerConfig.hooks'

/**
 * Google Drive picker.
 *
 * Identity split: the Picker iframe runs under the signed-in browser user's
 * Google token (Google only renders the picker for a signed-in user), while
 * the backend reads, downloads and folder-scans with the receptor's stored
 * service-account credentials. Because those two identities can see different
 * files, every pick is checked against the receptor on the backend before it
 * is handed to the next modal.
 */

const NOT_SHOWN: PickerResponse = {
  isShown: false,
  invalidCredentials: false,
  exceptions: '',
}

export const useLoadGoogleApi = (shouldLoad = true): boolean => {
  const apiStatus: Status = useScript(GOOGLE_API_SRC)
  const gsiStatus: Status = useScript(GOOGLE_GSI_CLIENT_SRC)
  const [pickerReady, setPickerReady] = useState<boolean>(false)

  useEffect(() => {
    if (apiStatus === 'ready' && !pickerReady && shouldLoad) {
      window.gapi.load('picker', () => setPickerReady(true))
    }
  }, [apiStatus, pickerReady, shouldLoad])

  return pickerReady && gsiStatus === 'ready'
}

export const useGooglePicker = (): ((
  config: PickerConfiguration,
  receptor?: any,
) => Promise<PickerResponse>) => {
  const { setFilesSelected, receptor: contextReceptor } =
    useGoogleDriveContext()
  const showPicker = useRef<boolean>(false)
  const scriptsReady = useLoadGoogleApi(!showPicker.current)
  const { openModal } = useModalState()
  const { startThrobber, stopThrobber } = useThrobberContext()
  // OAuth client ID and app ID come from ntrced's config, fetched once and
  // cached, so the picker cannot open until that first fetch has settled.
  const pickerConfig = useGoogleDrivePickerConfig()
  const ready = scriptsReady && !pickerConfig.isLoading
  const requestUserToken = useGoogleUserToken(pickerConfig.oauthClientId)
  const checkFileAccess = useCheckReceptorFileAccess()

  const createPicker = useCallback(
    (config: PickerConfiguration, receptor?: any): Promise<PickerResponse> => {
      const finish = (response: PickerResponse): PickerResponse => {
        showPicker.current = false
        return response
      }

      // Entry points that open the picker right after connecting the linker
      // pass no receptor; fall back to the account's Google Drive receptor.
      const activeLinker = receptor || contextReceptor
      if (
        !activeLinker?.credentials?.length &&
        !activeLinker?.getCredentials?.()?.length
      ) {
        openModal(ModalFormId.CONNECT_LINKER)
        return Promise.resolve(finish(NOT_SHOWN))
      }

      const exceptions =
        activeLinker?.exceptions || activeLinker?.getExceptions?.()
      if (exceptions?.length > 0) {
        return Promise.resolve(
          finish({ isShown: false, invalidCredentials: false, exceptions }),
        )
      }

      const iscredvalid =
        activeLinker?.iscredvalid !== undefined
          ? activeLinker?.iscredvalid
          : activeLinker?.getIscredvalid?.()
      if (!iscredvalid) {
        return Promise.resolve(
          finish({ isShown: false, invalidCredentials: true, exceptions: '' }),
        )
      }

      const receptorId: string =
        activeLinker?.id || activeLinker?.getId?.() || ''

      // Runs after the user confirms a selection in the picker. Drops every
      // item the receptor's own identity cannot read, tells the user whom to
      // share with, and only then opens the target modal.
      const onPicked = async (docs: google.picker.DocumentObject[]) => {
        let accepted = docs
        if (receptorId && docs.length > 0) {
          try {
            startThrobber()
            const response = await checkFileAccess(
              receptorId,
              docs.map((doc) => doc.id),
            )
            const { accessible, denied, receptorIdentity } =
              splitByReceptorAccess(docs, response)
            accepted = accessible
            if (denied.length > 0) {
              showInfoToast(
                buildDeniedAccessMessage(denied, receptorIdentity),
                {
                  autoClose: 15000,
                },
              )
            }
          } catch (e) {
            // Fail open: a backend hiccup must not block a pick. The download
            // will surface a real permission problem later.
            log.error('Google Drive access check failed', e)
            showInfoToast(
              'Could not verify that Trustero can read the selected items. Continuing without verification.',
            )
          } finally {
            stopThrobber()
          }
        }

        if (accepted.length === 0) {
          setFilesSelected?.([])
          return
        }
        setFilesSelected?.(accepted)
        openModal(config.modalId)
      }

      const callback = (tokendata: {
        action: string
        docs: google.picker.DocumentObject[]
      }) => {
        if (tokendata.action === google.picker.Action.PICKED) {
          showPicker.current = false
          void onPicked(tokendata.docs || [])
        } else if (tokendata.action === google.picker.Action.CANCEL) {
          showPicker.current = false
          setFilesSelected?.([])
        }
      }

      const buildPicker = (userToken: string): PickerResponse => {
        const mimeTypes = config.mimeTypes
          ? config.mimeTypes
          : GOOGLE_PICKER_SUPPORTED_FILE_TYPES

        const view = new google.picker.DocsView(config.viewId)
          .setIncludeFolders(true)
          .setMode('LIST')
          .setMimeTypes(mimeTypes)
          .setSelectFolderEnabled(config.selectFolderEnabled || false)

        const driveView = new google.picker.DocsView(config.viewId)
          .setIncludeFolders(true)
          .setMimeTypes(mimeTypes)
          .setSelectFolderEnabled(config.selectFolderEnabled || false)
          .setEnableDrives(true)

        const picker = new google.picker.PickerBuilder()
          .setOAuthToken(userToken)
          .setDeveloperKey(process.env.REACT_APP_GOOGLE_API_KEY || '')
          .setLocale('en')
          .addView(view)
          .addView(driveView)
          .setOrigin(window.location.origin)
          .setTitle('Select a file from Google Drive or Shared Drives')
          .setCallback(callback)
        // setAppId (the OAuth client's Cloud project number) is what lets an
        // app later read picked files with the *user's* drive.file token. We
        // never do that: the user token exists only to render the picker, and
        // every read/download runs on the backend under the receptor's
        // service account (verified per pick by CheckReceptorFileAccess). So
        // the picker renders and returns selections without it, and it is
        // intentionally optional here. Set google_picker_app_id in ntrced
        // config to send it anyway.
        pickerConfig.appId && picker.setAppId(pickerConfig.appId)
        config.selectableMimeTypes &&
          picker.setSelectableMimeTypes(config.selectableMimeTypes)
        config.allowMultiselect &&
          picker.enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        picker.build().setVisible(true)

        return { isShown: true, invalidCredentials: false, exceptions: '' }
      }

      // No `await` may precede this call: the Google sign-in popup has to open
      // inside the click's user-activation window or the browser blocks it.
      return requestUserToken()
        .then(buildPicker)
        .catch((e) => {
          if (
            e instanceof GoogleUserTokenError &&
            GOOGLE_USER_CANCELLED_CODES.has(e.code)
          ) {
            return finish(NOT_SHOWN)
          }
          log.error('Google sign-in for the Drive picker failed', e)
          showInfoToast(
            e instanceof GoogleUserTokenError && e.code === 'missing_client_id'
              ? 'The Google Drive picker is not configured: ntrced has no oauth_gdrive_client_id for this environment.'
              : 'Google sign-in is required to browse Google Drive. Allow the popup and try again.',
          )
          return finish(NOT_SHOWN)
        })
    },
    [
      openModal,
      setFilesSelected,
      contextReceptor,
      startThrobber,
      stopThrobber,
      requestUserToken,
      checkFileAccess,
      pickerConfig.appId,
    ],
  )

  const openPicker = useCallback(
    async (config: PickerConfiguration, receptor?: any) => {
      if (!ready) {
        log.warn(
          'Google Picker requested before Google scripts finished loading',
        )
        return NOT_SHOWN
      }
      if (showPicker.current) {
        return NOT_SHOWN
      }
      showPicker.current = true
      return createPicker(config, receptor)
    },
    [ready, createPicker],
  )

  return openPicker
}
